import { css, html, LitElement } from "lit"
import { couleurDuType } from "../transverse/couleursTypes.js"
import { PLACEHOLDER_POKEMON, urlSpritePokemon } from "../transverse/spritePokemon.js"
import { genererIdentifiantAleatoire } from "../transverse/Utilitaire.js"
import { palettePlanning } from "./stylePlanning.js"

/**
 * Carte repliable d'un pokémon du planning hebdomadaire.
 * Repliée : sprite + nom + type. Dépliée : PC (boosté / non boosté) + types d'attaquants recommandés.
 * Sans aucune de ces infos (ex : Spotlight Hour), la carte reste affichée mais n'est pas repliable.
 */
export default class CartePokemonPlanning extends LitElement {
  static properties = {
    pokemon: { type: Object },
    ouverte: { type: Boolean, reflect: true },
  }

  static styles = [
    palettePlanning,
    css`
      :host {
        display: block;
      }

      .carte {
        background-color: var(--planning-surface);
        border-radius: var(--border-radius-champ);
        overflow: hidden;
      }

      .entete {
        all: unset;
        box-sizing: border-box;
        display: flex;
        align-items: center;
        gap: 1.2rem;
        width: 100%;
        padding: 1.2rem 1.4rem;
        cursor: pointer;
        border-left: 3px solid var(--couleur-type, var(--planning-texte-doux));
      }

      .entete:focus-visible {
        outline: 2px solid var(--couleur-type, var(--planning-texte-doux));
        outline-offset: -2px;
      }

      .entete:disabled {
        cursor: default;
      }

      .avatar {
        flex: 0 0 auto;
        width: 4rem;
        height: 4rem;
        border-radius: 50%;
        object-fit: cover;
        background-color: var(--planning-surface-2);
      }

      .infos {
        flex: 1 1 auto;
        min-width: 0;
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
      }

      .nom {
        font-weight: bold;
        font-size: 1.6rem;
        line-height: 1.2;
      }

      .badge-type {
        align-self: flex-start;
        display: inline-flex;
        align-items: center;
        gap: 0.5rem;
        font-size: 1.2rem;
        padding: 0.2rem 0.9rem;
        border-radius: 5rem;
        color: var(--couleur-type, var(--planning-texte-doux));
        background-color: color-mix(in srgb, var(--couleur-type, var(--planning-texte-doux)) 16%, transparent);
        border: 1px solid color-mix(in srgb, var(--couleur-type, var(--planning-texte-doux)) 40%, transparent);
      }

      .badge-type .pastille {
        width: 0.7rem;
        height: 0.7rem;
        border-radius: 50%;
        background-color: var(--couleur-type, var(--planning-texte-doux));
      }

      .chevron {
        flex: 0 0 auto;
        width: 2rem;
        height: 2rem;
        color: var(--planning-texte-doux);
        transition: transform 0.22s ease;
      }

      :host([ouverte]) .chevron {
        transform: rotate(180deg);
      }

      .detail {
        display: grid;
        grid-template-rows: 0fr;
        transition: grid-template-rows 0.28s ease;
      }

      :host([ouverte]) .detail {
        grid-template-rows: 1fr;
      }

      .detail-interieur {
        overflow: hidden;
      }

      .detail-contenu {
        padding: 1.4rem 1.6rem 1.6rem;
        border-top: 2px solid var(--planning-bordure);
        display: flex;
        flex-direction: column;
        gap: 1.2rem;
      }

      .ligne-pc {
        display: flex;
        justify-content: flex-start;
        gap: 1rem;
        font-size: 1.4rem;
      }

      .ligne-pc .libelle {
        color: var(--planning-texte-doux);
      }

      .ligne-pc .valeur {
        font-weight: bold;
        white-space: nowrap;
      }

      .titre-attaquants {
        font-size: 1.3rem;
        color: var(--planning-texte-doux);
        margin: 0;
      }

      .liste-attaquants {
        display: flex;
        flex-wrap: wrap;
        gap: 0.6rem;
      }

      @media (prefers-reduced-motion: reduce) {
        .chevron,
        .detail {
          transition: none;
        }
      }
    `,
  ]

  constructor() {
    super()
    this.pokemon = null
    this.ouverte = false
    this.identifiantDetail = `detail-${genererIdentifiantAleatoire()}`
  }

  aDesDetails() {
    return Boolean(this.pokemon?.pcNonBooste || this.pokemon?.pcBooste || this.pokemon?.typesAttaquants?.length)
  }

  basculer() {
    if (!this.aDesDetails()) return
    this.ouverte = !this.ouverte
  }

  gererErreurImage(event) {
    event.target.src = PLACEHOLDER_POKEMON
  }

  formaterPc(plage) {
    return plage.replace(/\s*[-–]\s*/, " – ")
  }

  render() {
    if (!this.pokemon) {
      return html``
    }

    const { nom, type, pcNonBooste, pcBooste, typesAttaquants } = this.pokemon
    const aDesDetails = this.aDesDetails()

    return html`
      <div class="carte" style="--couleur-type: ${couleurDuType(type)}">
        <button
          class="entete"
          type="button"
          ?disabled=${!aDesDetails}
          aria-expanded=${aDesDetails ? (this.ouverte ? "true" : "false") : "false"}
          aria-controls=${this.identifiantDetail}
          @click=${this.basculer}
        >
          <img
            class="avatar"
            src=${urlSpritePokemon(nom)}
            alt=""
            loading="lazy"
            @error=${this.gererErreurImage}
          />
          <span class="infos">
            <span class="nom">${nom}</span>
            ${type ? html`<span class="badge-type"><span class="pastille"></span>${type}</span>` : ""}
          </span>
          ${
            aDesDetails
              ? html`
                <svg class="chevron" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                  <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
              `
              : ""
          }
        </button>
        <div class="detail" id=${this.identifiantDetail}>
          <div class="detail-interieur">
            <div class="detail-contenu">
              ${
                pcNonBooste
                  ? html`<div class="ligne-pc"><span class="libelle">PC non boosté</span><span class="valeur">${this.formaterPc(pcNonBooste)}</span></div>`
                  : ""
              }
              ${
                pcBooste ? html`<div class="ligne-pc"><span class="libelle">PC boosté</span><span class="valeur">${this.formaterPc(pcBooste)}</span></div>` : ""
              }
              ${
                typesAttaquants?.length
                  ? html`
                    <p class="titre-attaquants">Attaquants recommandés</p>
                    <div class="liste-attaquants">
                      ${typesAttaquants.map(
                        (typeAttaquant) => html`
                          <span class="badge-type" style="--couleur-type: ${couleurDuType(typeAttaquant)}">
                            <span class="pastille"></span>${typeAttaquant}
                          </span>
                        `,
                      )}
                    </div>
                  `
                  : ""
              }
            </div>
          </div>
        </div>
      </div>
    `
  }
}
customElements.define("p-carte-pokemon-planning", CartePokemonPlanning)
